// Attempts to extract job details (title, company, salary) from a posting URL.
//
// Strategy:
//   1. Call /api/fetch-job-posting (Cloudflare Pages Function) — server-side, no CORS limit.
//   2. Fall back to direct browser fetch for CORS-friendly sites / local dev.
//
// All failures are graceful — never blocks saving the link.

const SALARY_RES = [
  /\$\s*([\d,]+)\s*[kK]?\s*[-–—]+\s*\$?\s*([\d,]+)\s*[kK]?(?:\s*\/?\s*(?:yr|year|annual|annually))?/,
  /\$\s*([\d,]+)\s*[kK]?\s+to\s+\$?\s*([\d,]+)\s*[kK]?/,
  /USD\s*([\d,]+)\s*[-–—to]+\s*([\d,]+)/i,
  /([\d,]+)\s*[kK]\s*[-–—]+\s*([\d,]+)\s*[kK]/,
];

function extractSalaryText(text) {
  for (const re of SALARY_RES) {
    const m = re.exec(text);
    if (m) return m[0].trim();
  }
  return null;
}

// Browser-side HTML parser — used only in the direct-fetch fallback path.
function parseJobFromHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const result = {};

  for (const script of doc.querySelectorAll('script[type="application/ld+json"]')) {
    try {
      const raw = JSON.parse(script.textContent);
      const items = [raw, ...(Array.isArray(raw['@graph']) ? raw['@graph'] : [])];
      const posting = items.find(d => d && d['@type'] === 'JobPosting');
      if (posting) {
        if (posting.title) result.title = posting.title.trim();
        if (posting.hiringOrganization?.name) result.company = posting.hiringOrganization.name.trim();
        const bs = posting.baseSalary?.value;
        if (bs) {
          if (bs.minValue && bs.maxValue) {
            result.salary = `$${Number(bs.minValue).toLocaleString()} - $${Number(bs.maxValue).toLocaleString()}`;
          } else if (bs.value) {
            result.salary = `$${Number(bs.value).toLocaleString()}`;
          }
        }
        if (result.title || result.company) break;
      }
    } catch {}
  }

  if (!result.title) {
    result.title =
      doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ||
      doc.querySelector('h1')?.textContent?.trim() ||
      doc.title?.trim() ||
      null;
    if (result.title) result.title = result.title.split(/\s*[|·—]\s*/)[0].trim();
  }

  if (!result.salary) {
    const bodyText = (doc.body?.textContent || '').slice(0, 10000);
    result.salary = extractSalaryText(bodyText) || null;
  }

  return (result.title || result.company || result.salary) ? result : null;
}

async function fetchJobPostingDetails(url) {
  if (!url || !url.startsWith('http')) return null;

  // 1. Server-side proxy (no CORS, works on Greenhouse, Lever, Workday, etc.)
  try {
    const session = await window.Auth.getSession();
    const token = session?.access_token;
    if (token) {
      const res = await fetch('/api/fetch-job-posting', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ url }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!data.corsBlocked && !data.empty && !data.error) return data;
        if (data.empty) return { empty: true };
      }
    }
  } catch {}

  // 2. Direct browser fetch fallback (works only if the site allows CORS)
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) return { corsBlocked: true };
    const html = await res.text();
    return parseJobFromHtml(html) || { empty: true };
  } catch {
    return { corsBlocked: true };
  }
}
